import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { Loader2, Github, GitBranch, ExternalLink, Server, CheckCircle2, RefreshCw, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function EcosystemPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/ecosystem/scan`, auth)
      .then(({ data }) => setRepos(data.repos))
      .catch(() => toast.error("Échec du scan de l'écosystème"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const importRepo = async (r) => {
    setImporting(r.path);
    try {
      const { data } = await axios.post(`${API_URL}/api/ecosystem/import`, { path: r.path, name: r.name }, auth);
      toast.success("Projet importé");
      navigate(`/dashboard/projects/${data.id}`);
    } catch (e) {
      toast.error("Échec de l'import");
    } finally {
      setImporting(null);
    }
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Écosystème</h1>
            <p className="text-slate-500 text-sm mt-0.5">Tous les dépôts détectés sur votre serveur — GitHub, branche, déploiement</p>
          </div>
          <div className="flex-1" />
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Re-scanner
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {repos.map((r) => (
              <div key={r.path} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 truncate">{r.name}</span>
                      {r.dirty && <span title="Modifications non commitées"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /></span>}
                    </div>
                    {r.github_url ? (
                      <a href={r.github_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600 mt-0.5">
                        <Github className="w-3.5 h-3.5" /> {r.github_url.replace("https://github.com/", "")}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-xs text-slate-400">Pas de dépôt distant</span>}
                  </div>
                  {r.project_id ? (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/dashboard/projects/${r.project_id}`)}>
                      Ouvrir <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => importRepo(r)} disabled={importing === r.path} className="bg-indigo-600 hover:bg-indigo-700">
                      {importing === r.path ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Importer"}
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                  {r.branch && <span className="inline-flex items-center gap-1"><GitBranch className="w-3.5 h-3.5" /> {r.branch}</span>}
                  {(r.ahead > 0 || r.behind > 0) && (
                    <span className="text-amber-600">{r.ahead > 0 ? `↑${r.ahead}` : ""} {r.behind > 0 ? `↓${r.behind}` : ""}</span>
                  )}
                  {r.deployed ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600"><Server className="w-3.5 h-3.5" /> déployé</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-slate-400"><Server className="w-3.5 h-3.5" /> non déployé</span>
                  )}
                </div>

                {r.last_commit?.subject && (
                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 text-xs text-slate-500">
                    <span className="font-mono text-slate-400">{r.last_commit.hash}</span> {r.last_commit.subject}
                    <span className="text-slate-400"> · {r.last_commit.when} · {r.last_commit.author}</span>
                  </div>
                )}
                {r.services?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.services.map((s) => (
                      <span key={s} className="text-[10px] bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
